import { useEffect, useState } from "react";

import { useParams } from "@tanstack/react-router";

import { apiClient } from "@/api/apiClient";
import { getUserInitialAnswersApiV1ReportGeneratorTemplatesIdCasesCaseNumberGetUserInitialAnswersPost } from "@/api/generated/sdk.gen";
import type { ReportGeneratorInitialAnswersResponseSectionSchema } from "@/api/generated/types.gen";
import { Button } from "@/components/ui/button";

type ModuleSummary = {
  module_name: string;
  summaries_from_doc_sorter: string | null;
  summaries_from_assessment_scribe: string | null;
};

const ResultJsonPage = () => {
  const { id: caseNumber, templateId } = useParams({ strict: false }) as {
    id: string;
    templateId: string;
  };
  const [jsonData, setJsonData] = useState<
    ModuleSummary[] | { error: string } | null
  >(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchAndDisplayJson = async () => {
      if (!caseNumber || !templateId) {
        setJsonData({ error: "Case number and template ID are required" });
        return;
      }

      try {
        const response =
          await getUserInitialAnswersApiV1ReportGeneratorTemplatesIdCasesCaseNumberGetUserInitialAnswersPost(
            {
              client: apiClient,
              path: {
                id: templateId,
                case_number: caseNumber,
              },
            }
          );

        if ("error" in response && response.error) {
          console.error("Failed to fetch user initial answers:", response.error);
          setJsonData({ error: "Failed to fetch user initial answers" });
          return;
        }

        if (!response.data) {
          setJsonData({ error: "No data found" });
          return;
        }

        // Flatten the structure: extract all modules from all sections
        const modules: ModuleSummary[] = [];

        Object.entries(response.data).forEach(
          ([_, section]: [
            string,
            ReportGeneratorInitialAnswersResponseSectionSchema,
          ]) => {
            Object.entries(section.modules).forEach(([moduleName, module]) => {
              modules.push({
                module_name: moduleName,
                summaries_from_doc_sorter: module.summaries_from_doc_sorter ?? null,
                summaries_from_assessment_scribe:
                  module.summaries_from_assessment_scribe ?? null,
              });
            });
          }
        );

        setJsonData(modules);
      } catch (error) {
        console.error("Error fetching user initial answers:", error);
        setJsonData({ error: "Failed to fetch user initial answers" });
      }
    };

    fetchAndDisplayJson();
  }, [caseNumber, templateId]);

  const handleCopy = async () => {
    if (jsonData === null) return;

    const jsonString = JSON.stringify(jsonData, null, 2);
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (jsonData === null) {
    return (
      <div style={{ padding: "20px" }}>
        <pre>Loading...</pre>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={handleCopy} variant="outline" size="sm">
          {copied ? "Copied!" : "Copy JSON"}
        </Button>
      </div>
      <pre
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          padding: "16px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
          fontSize: "14px",
          lineHeight: "1.5",
          overflow: "auto",
          maxHeight: "calc(100vh - 120px)",
          userSelect: "text",
        }}
      >
        {JSON.stringify(jsonData, null, 2)}
      </pre>
    </div>
  );
};

export default ResultJsonPage;
